import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TableFooter,
  Tabs,
  Tab,
  Paper,
  Tooltip as MuiTooltip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useTranslate } from 'react-admin';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { TableChart, ShowChart } from '@mui/icons-material';
import { ReportLayout } from '../shared/ReportLayout';
import { ReportFilters } from '../shared/ReportFilters';
import { useReportData } from '../../../hooks/useReportData';
import { useReportExport } from '../../../hooks/useReportExport';
import { useCanSeeTotalSessions } from '../../../hooks/useCanSeeTotalSessions';
import { formatCurrency, formatNumber, formatDate, getDateRangePresets } from '../../../utils/reportHelpers';

interface VendorSummary {
  total_vendors: number;
  total_sessions: number;
  charged_sessions: number;
  total_revenue: number;
  best_performer: string;
  avg_uptime: number;
}

interface VendorMetric {
  vendor_id: string;
  vendor_name: string;
  vendor_type: string;
  total_sessions: number;
  charged_sessions: number;
  total_revenue: number;
  avg_session_duration_minutes: number;
  uptime_rate: number;
  total_sites: number;
  total_chargers: number;
}

interface VendorReportData {
  summary: VendorSummary;
  vendors: VendorMetric[];
  time_series: any[];
}

const VENDOR_COLORS = ['#009A75', '#2196F3', '#FFB800', '#FF6B6B', '#9C27B0', '#00BCD4', '#4CAF50', '#FF9800'];

// TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const tableCellWrapSx = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  verticalAlign: 'top',
};

const tableDetailsSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    ...tableCellWrapSx,
    px: { xs: 1, sm: 2 },
    py: { xs: 1, sm: 1.25 },
    fontSize: { xs: '0.75rem', sm: '0.875rem' },
  },
};

const mobileRowCardSx = {
  p: 1.5,
  borderRadius: 1,
  border: '1px solid',
  borderColor: 'divider',
  backgroundColor: '#FAFAFA',
};

const mobileLabelSx = {
  fontSize: '0.7rem',
  color: 'text.secondary',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
};

const mobileValueSx = {
  fontSize: '0.875rem',
  fontWeight: 500,
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

export const VendorPerformanceReport: React.FC = () => {
  const translate = useTranslate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const presets = getDateRangePresets();
  const canSeeTotalSessions = useCanSeeTotalSessions();

  // Filters state
  const [startDate, setStartDate] = useState(presets.last30Days.start);
  const [endDate, setEndDate] = useState(presets.last30Days.end);

  // Tab state (0 = table, 1 = charts)
  const [tabValue, setTabValue] = useState(0);

  // Table sorting state
  const [orderBy, setOrderBy] = useState<keyof VendorMetric>('total_sessions');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch data
  const { data, loading, error, refetch } = useReportData<VendorReportData>({
    endpoint: '/api/backend/reports/vendor-performance',
    filters: {
      start_date: startDate,
      end_date: endDate,
    },
  });

  // Export functionality. The hook surfaces success/empty/failure toasts via
  // react-admin so the user always knows what happened — see useReportExport.
  const { exportToCSV, exportToExcel, exporting } = useReportExport();

  const handleExportCSV = () => {
    if (!data?.vendors) return;

    exportToCSV(
      data.vendors,
      'vendor-performance',
      [
        { key: 'vendor_name', label: 'Vendor' },
        { key: 'vendor_type', label: 'Type' },
        ...(canSeeTotalSessions ? [{ key: 'total_sessions', label: 'Total Sessions', format: 'number' as const }] : []),
        { key: 'charged_sessions', label: 'Charged Sessions', format: 'number' },
        { key: 'total_revenue', label: 'Revenue', format: 'currency' },
        { key: 'avg_session_duration_minutes', label: 'Avg Duration (min)', format: 'number' },
        { key: 'total_sites', label: 'Sites', format: 'number' },
        { key: 'total_chargers', label: 'Chargers', format: 'number' },
      ]
    );
  };

  const handleExportExcel = async () => {
    if (!data) return;

    // Build filters object
    const filters: any = {
      startDate,
      endDate,
    };

    await exportToExcel(
      data.vendors,
      'vendor-performance',
      {
        columns: [
          { key: 'vendor_name', label: translate('reports.table.vendor', { fallback: 'Vendor' }) },
          { key: 'vendor_type', label: translate('reports.table.type', { fallback: 'Type' }) },
          ...(canSeeTotalSessions ? [{ key: 'total_sessions', label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }), format: 'number' as const }] : []),
          { key: 'charged_sessions', label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }), format: 'number' },
          { key: 'total_revenue', label: translate('reports.table.revenue', { fallback: 'Revenue' }), format: 'currency' },
          { key: 'avg_session_duration_minutes', label: translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' }), format: 'number' },
          { key: 'total_sites', label: translate('reports.table.sites', { fallback: 'Sites' }), format: 'number' },
          { key: 'total_chargers', label: translate('reports.table.chargers', { fallback: 'Chargers' }), format: 'number' },
        ],
        summary: {
          title: translate('reports.vendorPerformance.title', { fallback: 'Vendor Performance' }),
          data: [
            {
              label: translate('reports.kpi.totalVendors', { fallback: 'Total Vendors' }),
              value: formatNumber(data.summary.total_vendors, 0)
            },
            ...(canSeeTotalSessions ? [{
              label: translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' }),
              value: formatNumber(data.summary.total_sessions, 0)
            }] : []),
            {
              label: translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' }),
              value: formatNumber(data.summary.charged_sessions, 0)
            },
            {
              label: translate('reports.kpi.totalRevenue', { fallback: 'Total Revenue' }),
              value: formatCurrency(data.summary.total_revenue)
            },
            {
              label: translate('reports.kpi.bestPerformer', { fallback: 'Best Performer' }),
              value: data.summary.best_performer
            },
            {
              label: translate('reports.kpi.avgUptime', { fallback: 'Avg Uptime' }),
              value: formatNumber(data.summary.avg_uptime, 1) + '%'
            },
          ]
        },
        sheetName: translate('reports.tabs.dataTable', { fallback: 'Data' }),
        includeTimestamp: true,
        translate: translate,
        filters: filters
      }
    );
  };

  // Sort data
  const sortedData = useMemo(() => {
    if (!data?.vendors) return [];

    return [...data.vendors].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data?.vendors, orderBy, order]);

  // Calculate totals for footer
  const totals = useMemo(() => {
    if (!data?.vendors || data.vendors.length === 0) {
      return {
        total_sessions: 0,
        charged_sessions: 0,
        total_revenue: 0,
        total_sites: 0,
        total_chargers: 0,
      };
    }

    const totalSessions = data.vendors.reduce((sum, v) => sum + v.total_sessions, 0);
    const chargedSessions = data.vendors.reduce((sum, v) => sum + v.charged_sessions, 0);
    const totalRevenue = data.vendors.reduce((sum, v) => sum + v.total_revenue, 0);
    const totalSites = data.vendors.reduce((sum, v) => sum + v.total_sites, 0);
    const totalChargers = data.vendors.reduce((sum, v) => sum + v.total_chargers, 0);

    return {
      total_sessions: totalSessions,
      charged_sessions: chargedSessions,
      total_revenue: totalRevenue,
      total_sites: totalSites,
      total_chargers: totalChargers,
    };
  }, [data?.vendors]);

  const handleSort = (property: keyof VendorMetric) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const topRevenueVendorData = useMemo(() => {
    if (!data?.vendors) return [];
    const byRevenue = [...data.vendors].sort((a, b) => b.total_revenue - a.total_revenue);
    return isMobile ? byRevenue.slice(0, 5) : byRevenue;
  }, [data?.vendors, isMobile]);

  const topSessionVendorData = useMemo(() => {
    if (!data?.vendors) return [];
    const bySessions = [...data.vendors].sort((a, b) => b.total_sessions - a.total_sessions);
    return isMobile ? bySessions.slice(0, 5) : bySessions;
  }, [data?.vendors, isMobile]);

  const trendVendors = useMemo(() => {
    if (!data?.vendors) return [];
    if (!isMobile) return data.vendors;
    return [...data.vendors].sort((a, b) => b.total_revenue - a.total_revenue).slice(0, 4);
  }, [data?.vendors, isMobile]);

  return (
    <ReportLayout
      title={translate('reports.vendorPerformance.title', { fallback: 'Vendor Performance' })}
      description={translate('reports.vendorPerformance.subtitle', { fallback: 'Cross-vendor performance comparison' })}
      filters={
        <ReportFilters
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onRefresh={refetch}
          showGroupByFilter={false}
        />
      }
      exportButtons={{
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        exporting,
        disabled: !data || loading,
      }}
      loading={loading}
      error={error}
    >
      {data && (
        <Box sx={{ maxWidth: '100%', minWidth: 0 }}>
          {/* Tabs */}
          <Card sx={{ mb: 3, overflow: 'hidden', minWidth: 0 }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', px: { xs: 1.5, sm: 2 }, pt: 1.5, minWidth: 0 }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant={isMobile ? "fullWidth" : "scrollable"}
                scrollButtons={isMobile ? false : "auto"}
                sx={{
                  minWidth: 0,
                  minHeight: { xs: 40, sm: 48 },
                  '& .MuiTab-root': {
                    minWidth: { xs: 0, sm: 90 },
                    minHeight: { xs: 40, sm: 48 },
                    px: { xs: 0.5, sm: 2 },
                    py: { xs: 0.75, sm: 1.5 },
                    fontSize: { xs: '0.7rem', sm: '0.875rem' },
                    textTransform: 'none',
                    lineHeight: 1.2,
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  },
                }}
              >
                <Tab icon={<TableChart />} iconPosition="start" label={translate('reports.tabs.dataTable', { fallback: 'Data Table' })} />
                <Tab icon={<ShowChart />} iconPosition="start" label={translate('reports.tabs.charts', { fallback: 'Charts' })} />
              </Tabs>
            </Box>

            {/* Data Table Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, py: { xs: 2, sm: 3 } }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: '#1A1A1A',
                    mb: 2,
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {translate('reports.vendorDetails', { fallback: 'Detailed Vendor Metrics' })} ({sortedData.length} vendors)
                </Typography>

                {isMobile ? (
                  <Box sx={{ display: 'grid', gap: 1.25 }}>
                    <Paper variant="outlined" sx={mobileRowCardSx}>
                      <Typography sx={{ ...mobileLabelSx, mb: 1 }}>
                        {translate('reports.table.total', { fallback: 'Total' })}
                      </Typography>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                        {canSeeTotalSessions && (
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(totals.total_sessions, 0)}</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.charged_sessions, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatCurrency(totals.total_revenue)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.sites', { fallback: 'Sites' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.total_sites, 0)}</Typography>
                        </Box>
                        <Box>
                          <Typography sx={mobileLabelSx}>{translate('reports.table.chargers', { fallback: 'Chargers' })}</Typography>
                          <Typography sx={mobileValueSx}>{formatNumber(totals.total_chargers, 0)}</Typography>
                        </Box>
                      </Box>
                    </Paper>

                    {sortedData.map((vendor) => (
                      <Paper key={vendor.vendor_id} variant="outlined" sx={mobileRowCardSx}>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.25 }}>
                          {translate('reports.table.vendor', { fallback: 'Vendor' })}
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, fontWeight: 700, mb: 0.75 }}>
                          {vendor.vendor_name}
                        </Typography>
                        <Typography sx={{ ...mobileLabelSx, mb: 0.25 }}>
                          {translate('reports.table.type', { fallback: 'Type' })}
                        </Typography>
                        <Typography sx={{ ...mobileValueSx, mb: 1 }}>
                          {vendor.vendor_type}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          {canSeeTotalSessions && (
                            <Box>
                              <Typography sx={mobileLabelSx}>{translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}</Typography>
                              <Typography sx={mobileValueSx}>{formatNumber(vendor.total_sessions, 0)}</Typography>
                            </Box>
                          )}
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(vendor.charged_sessions, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.revenue', { fallback: 'Revenue' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatCurrency(vendor.total_revenue)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(vendor.avg_session_duration_minutes, 1)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.sites', { fallback: 'Sites' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(vendor.total_sites, 0)}</Typography>
                          </Box>
                          <Box>
                            <Typography sx={mobileLabelSx}>{translate('reports.table.chargers', { fallback: 'Chargers' })}</Typography>
                            <Typography sx={mobileValueSx}>{formatNumber(vendor.total_chargers, 0)}</Typography>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0', maxHeight: 600 }}>
                      <Table stickyHeader sx={tableDetailsSx}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F5F5F5' }}>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'vendor_name'}
                                direction={orderBy === 'vendor_name' ? order : 'asc'}
                                onClick={() => handleSort('vendor_name')}
                              >
                                {translate('reports.table.vendor', { fallback: 'Vendor' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell>
                              <TableSortLabel
                                active={orderBy === 'vendor_type'}
                                direction={orderBy === 'vendor_type' ? order : 'asc'}
                                onClick={() => handleSort('vendor_type')}
                              >
                                {translate('reports.table.type', { fallback: 'Type' })}
                              </TableSortLabel>
                            </TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">
                                <TableSortLabel
                                  active={orderBy === 'total_sessions'}
                                  direction={orderBy === 'total_sessions' ? order : 'asc'}
                                  onClick={() => handleSort('total_sessions')}
                                >
                                  {translate('reports.kpi.totalSessions', { fallback: 'Total Sessions' })}
                                </TableSortLabel>
                              </TableCell>
                            )}
                            <TableCell align="right">
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                                <TableSortLabel
                                  active={orderBy === 'charged_sessions'}
                                  direction={orderBy === 'charged_sessions' ? order : 'asc'}
                                  onClick={() => handleSort('charged_sessions')}
                                >
                                  {translate('reports.kpi.chargedSessions', { fallback: 'Charged Sessions' })}
                                </TableSortLabel>
                                <MuiTooltip
                                  title={translate('reports.kpi.chargedSessionsTooltip', {
                                    fallback: 'Sessions that actually delivered energy (kWh > 0).',
                                  })}
                                  arrow
                                  placement="top"
                                >
                                  <Box component="span" sx={{ cursor: 'help', color: '#9E9E9E' }}>ⓘ</Box>
                                </MuiTooltip>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'total_revenue'}
                                direction={orderBy === 'total_revenue' ? order : 'asc'}
                                onClick={() => handleSort('total_revenue')}
                              >
                                {translate('reports.table.revenue', { fallback: 'Revenue' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'avg_session_duration_minutes'}
                                direction={orderBy === 'avg_session_duration_minutes' ? order : 'asc'}
                                onClick={() => handleSort('avg_session_duration_minutes')}
                              >
                                {translate('reports.table.avgDuration', { fallback: 'Avg Duration (min)' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'total_sites'}
                                direction={orderBy === 'total_sites' ? order : 'asc'}
                                onClick={() => handleSort('total_sites')}
                              >
                                {translate('reports.table.sites', { fallback: 'Sites' })}
                              </TableSortLabel>
                            </TableCell>
                            <TableCell align="right">
                              <TableSortLabel
                                active={orderBy === 'total_chargers'}
                                direction={orderBy === 'total_chargers' ? order : 'asc'}
                                onClick={() => handleSort('total_chargers')}
                              >
                                {translate('reports.table.chargers', { fallback: 'Chargers' })}
                              </TableSortLabel>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {sortedData.map((vendor) => (
                            <TableRow key={vendor.vendor_id} hover>
                              <TableCell>{vendor.vendor_name}</TableCell>
                              <TableCell>{vendor.vendor_type}</TableCell>
                              {canSeeTotalSessions && (
                                <TableCell align="right">{formatNumber(vendor.total_sessions, 0)}</TableCell>
                              )}
                              <TableCell align="right">{formatNumber(vendor.charged_sessions, 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(vendor.total_revenue)}</TableCell>
                              <TableCell align="right">{formatNumber(vendor.avg_session_duration_minutes, 1)}</TableCell>
                              <TableCell align="right">{formatNumber(vendor.total_sites, 0)}</TableCell>
                              <TableCell align="right">{formatNumber(vendor.total_chargers, 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                        <TableFooter>
                          <TableRow
                            sx={{
                              '& td': {
                                position: 'sticky',
                                bottom: 0,
                                backgroundColor: '#F5F5F5',
                                borderTop: '2px solid #E0E0E0',
                                fontWeight: 600,
                                zIndex: 2,
                              }
                            }}
                          >
                            <TableCell colSpan={2}>{translate('reports.table.total', { fallback: 'Total' })}</TableCell>
                            {canSeeTotalSessions && (
                              <TableCell align="right">{formatNumber(totals.total_sessions, 0)}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(totals.charged_sessions, 0)}</TableCell>
                            <TableCell align="right">{formatCurrency(totals.total_revenue)}</TableCell>
                            <TableCell align="right">-</TableCell>
                            <TableCell align="right">{formatNumber(totals.total_sites, 0)}</TableCell>
                            <TableCell align="right">{formatNumber(totals.total_chargers, 0)}</TableCell>
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Charts Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: { xs: 1.5, sm: 3 }, pb: { xs: 2, sm: 3 }, minWidth: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, 1fr)' }, gap: 3, mb: 3, mt: 3, minWidth: 0 }}>
                  {/* Vendor Comparison - Sessions */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.vendorSessions', { fallback: 'Sessions by Vendor' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                      {isMobile ? (
                        <BarChart data={topSessionVendorData} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            tickFormatter={(value) => formatNumber(value, 0)}
                          />
                          <YAxis
                            type="category"
                            dataKey="vendor_name"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            width={90}
                            tickFormatter={(value) => String(value).slice(0, 14)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatNumber(value, 0)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey={canSeeTotalSessions ? 'total_sessions' : 'charged_sessions'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[0, 6, 6, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={topSessionVendorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            dataKey="vendor_name"
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            tickFormatter={(value) => formatNumber(value, 0)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatNumber(value, 0)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey={canSeeTotalSessions ? 'total_sessions' : 'charged_sessions'} fill="#009A75" name={canSeeTotalSessions ? 'Sessions' : 'Charged Sessions'} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>

                  {/* Vendor Comparison - Revenue */}
                  <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: '#1A1A1A',
                        mb: 3,
                        fontSize: { xs: '1rem', sm: '1.25rem' },
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {translate('reports.charts.vendorRevenue', { fallback: 'Revenue by Vendor' })}
                    </Typography>
                    <ResponsiveContainer width="100%" height={isMobile ? 300 : 300}>
                      {isMobile ? (
                        <BarChart data={topRevenueVendorData} layout="vertical" margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            type="number"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                          />
                          <YAxis
                            type="category"
                            dataKey="vendor_name"
                            tick={{ fontSize: 10 }}
                            stroke="#606060"
                            width={90}
                            tickFormatter={(value) => String(value).slice(0, 14)}
                          />
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="total_revenue" fill="#2196F3" name="Revenue" radius={[0, 6, 6, 0]} />
                        </BarChart>
                      ) : (
                        <BarChart data={topRevenueVendorData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                          <XAxis
                            dataKey="vendor_name"
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            interval={0}
                          />
                          <YAxis
                            tick={{ fontSize: 12 }}
                            stroke="#606060"
                            tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                          />
                          <Tooltip
                            formatter={(value: number) => formatCurrency(value)}
                            contentStyle={{
                              backgroundColor: '#FFFFFF',
                              border: '1px solid #E0E0E0',
                              borderRadius: 8,
                            }}
                          />
                          <Bar dataKey="total_revenue" fill="#2196F3" name="Revenue" radius={[8, 8, 0, 0]} />
                        </BarChart>
                      )}
                    </ResponsiveContainer>
                  </Box>
                </Box>

                {/* Revenue Trends Over Time */}
                <Box sx={{ backgroundColor: '#FAFAFA', borderRadius: 2, p: { xs: 2, sm: 3 }, minWidth: 0 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: '#1A1A1A',
                      mb: 3,
                      fontSize: { xs: '1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                    }}
                  >
                    {translate('reports.charts.revenueTrends', { fallback: 'Revenue Trends by Vendor' })}
                  </Typography>
                  <ResponsiveContainer width="100%" height={isMobile ? 260 : 300}>
                    <LineChart data={data.time_series}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" />
                      <XAxis
                        dataKey="date"
                        tickFormatter={(date) => formatDate(date)}
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        interval={isMobile ? 'preserveStartEnd' : 0}
                        minTickGap={isMobile ? 24 : 12}
                      />
                      <YAxis
                        tick={{ fontSize: isMobile ? 10 : 12 }}
                        stroke="#606060"
                        tickFormatter={(value) => `$${formatNumber(value, 0)}`}
                        width={isMobile ? 42 : 64}
                      />
                      <Tooltip
                        formatter={(value: number) => formatCurrency(value)}
                        labelFormatter={(date) => formatDate(date, 'long')}
                        contentStyle={{
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E0E0E0',
                          borderRadius: 8,
                        }}
                      />
                      <Legend
                        verticalAlign={isMobile ? 'bottom' : 'top'}
                        align="center"
                        wrapperStyle={{ fontSize: isMobile ? 10 : 12 }}
                      />
                      {trendVendors.map((vendor, index) => (
                        <Line
                          key={vendor.vendor_id}
                          type="monotone"
                          dataKey={vendor.vendor_name}
                          stroke={VENDOR_COLORS[index % VENDOR_COLORS.length]}
                          strokeWidth={isMobile ? 1.75 : 2}
                          dot={{ r: isMobile ? 2.5 : 3 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              </Box>
            </TabPanel>
          </Card>
        </Box>
      )}
    </ReportLayout>
  );
};
